import { Helmet } from "react-helmet-async";
import { useState } from "react";
// @mui
import { LoadingButton } from "@mui/lab";
import {
  Container,
  Grid,
  Card,
  Stack,
  Typography,
  Box,
  Divider,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
// components
import { useSettingsContext } from "../components/settings";
import { useSnackbar } from "../components/snackbar";
import { Icon } from "@iconify/react";
import { getUserCheckInDetails, getUserWeightEma8Details } from "../controllers/checkin";
import moment from "moment";

// Types
export type Level = "Beginner Phase 1" | "Beginner Phase 2" | "Intermediate";
export type Gender = "male" | "female";
export type Goal = "Lose Fat" | "Build Muscle" | "Maintain";

export interface TdeeEntry {
  estimateSmoothed: number;
  confidence: number;
}

export interface PrecisionTdeeEntry {
  date: string;
  estimateSmoothed: number | null;
  sd: number | null;
  insufficient: boolean;
}

export interface CheckInUserInfo {
  gender: Gender;
  level: Level;
  goal: Goal;
  bodyFat: number;
  heightCm: number;
  currentCalorieTarget: number;
  tdee?: TdeeEntry;
}

export interface CheckInRecord {
  createdOn: string;
  calorieTarget: number;
  lbsChangePerWeek?: number;
  precisionTdee?: PrecisionTdeeEntry;
  previousCalorieTarget?: number;
  goal: Goal;
  [key: string]: any;
}

// The 7 days leading up to the record date: suffix 1 is 7 days before, suffix 7 is the day before.
// Weight change and the outer TDEE column are derived from this window only.
const DAY_SUFFIXES = [1, 2, 3, 4, 5, 6, 7];

// Check-ins within this window get the full daily breakdown; older ones are summarised.
const RECENT_MONTHS = 3;

// The record date itself, shown after the window for reference but not part of it.
const RECORD_DAY_SUFFIX = 8;
const DISPLAY_SUFFIXES = [...DAY_SUFFIXES, RECORD_DAY_SUFFIX];

const dateForSuffix = (createdOn: string, suffix: number) =>
  moment(createdOn, "YYYY-MM-DD").subtract(8 - suffix, "days").format("YYYY-MM-DD");

const recordDayCellSx = (suffix: number) =>
  suffix === RECORD_DAY_SUFFIX
    ? { bgcolor: "action.selected", fontWeight: "fontWeightBold", borderLeft: "2px solid", borderLeftColor: "divider" }
    : undefined;

const formatValue = (value: number) => (value ? Number(value).toFixed(2) : "-");

const formatTdee = (value: number) => (value ? String(Math.round(value)) : "-");

const resolvePrecisionTdee = (entries: PrecisionTdeeEntry[], checkInDate: string) => {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    if (entries[index].date <= checkInDate) return entries[index];
  }

  return undefined;
};

const formatPrecisionTdee = (entry?: PrecisionTdeeEntry) => {
  if (!entry) return "–";
  if (entry.insufficient) return "calibrating";
  if (entry.estimateSmoothed === null) return "–";

  const estimate = Math.round(entry.estimateSmoothed).toLocaleString("en-US");
  if (entry.sd === null) return estimate;
  return `${estimate} ± ${Math.round(entry.sd).toLocaleString("en-US")}`;
};

function PrecisionTdeeValue({ entry }: { entry?: PrecisionTdeeEntry }) {
  const value = formatPrecisionTdee(entry);
  if (!entry) return <>{value}</>;

  const tooltip = entry.insufficient
    ? "not enough logged data for a data-driven estimate"
    : `shadow row ${entry.date}`;

  return (
    <Tooltip title={tooltip}>
      <Box component="span">{value}</Box>
    </Tooltip>
  );
}

// Older check-ins stored a weekly body-weight multiplier in this field. Newer
// records store an absolute weekly pace, so keep the units explicit for both.
const LEGACY_PACE_MAX_MAGNITUDE = 0.01;

const formatPace = (value?: number) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  if (value === 0) return "0 lbs/week (Maintain)";

  const sign = value > 0 ? "+" : "";
  if (Math.abs(value) <= LEGACY_PACE_MAX_MAGNITUDE) {
    return `${sign}${(value * 100).toFixed(2)}% body weight/week (Legacy)`;
  }

  return `${sign}${value.toFixed(2)} lbs/week`;
};

// Confidence comes back as a 0-1 ratio.
const formatConfidence = (value?: number) => {
  if (value === undefined || value === null) return "-";
  return `${Math.round(value * 100)}%`;
};

// Days without an EMA reading are skipped, so the change spans the first and last
// day that actually has data rather than fixed window edges.
const formatWeightChange = (record: CheckInRecord) => {
  const values: number[] = DAY_SUFFIXES.map((suffix) => record[`weightEma${suffix}`]).filter(
    (value) => typeof value === "number" && value > 0
  );

  if (values.length < 2) return "-";
  return (values[values.length - 1] - values[0]).toFixed(4);
};

// The TDEE reading as it stood on the day the check-in was recorded.
const formatRecordDayTdee = (record: CheckInRecord) => {
  const estimate = record[`tdeeEstimateSmoothed${RECORD_DAY_SUFFIX}`];

  if (!estimate) return "-";
  return `${formatTdee(estimate)} (${formatConfidence(record[`tdeeConfidence${RECORD_DAY_SUFFIX}`])})`;
};

const formatCalorieTarget = (record: CheckInRecord) => {
  if (record.previousCalorieTarget === undefined) return `${record.calorieTarget} (New)`;

  const delta = record.calorieTarget - record.previousCalorieTarget;
  if (delta === 0) return `${record.calorieTarget} (No Change)`;
  return `${record.calorieTarget} (${delta > 0 ? "+" : ""}${delta})`;
};

type RecordCardProps = {
  record: CheckInRecord;
};

function RecordCard({ record }: RecordCardProps) {
  const [open, setOpen] = useState(false);

  const summary = [
    { label: "Date", value: record.createdOn },
    { label: "Calorie Target", value: formatCalorieTarget(record) },
    { label: "Goal", value: record.goal },
    { label: "Pace", value: formatPace(record.lbsChangePerWeek) },
    { label: "Weight Change", value: formatWeightChange(record) },
    { label: "TDEE (Confidence)", value: formatRecordDayTdee(record) },
    { label: "Precision TDEE (shadow)", value: <PrecisionTdeeValue entry={record.precisionTdee} /> },
  ];

  return (
    <Card variant="outlined">
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, flex: 1 }}>
          {summary.map((item) => (
            <Stack key={item.label} spacing={0.25} sx={{ minWidth: 140 }}>
              <Typography variant="caption" color="text.secondary">
                {item.label}
              </Typography>
              <Typography variant="subtitle2" sx={{ whiteSpace: "nowrap" }}>
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Box>
        <IconButton size="small">
          <Icon
            icon={open ? "eva:arrow-ios-upward-fill" : "eva:arrow-ios-downward-fill"}
            width={20}
            height={20}
          />
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 2 }}>
    <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Metric</TableCell>
                {DISPLAY_SUFFIXES.map((suffix) => (
                  <TableCell
                    key={suffix}
                    align="center"
                    sx={{ whiteSpace: "nowrap", ...recordDayCellSx(suffix) }}
                  >
                    {dateForSuffix(record.createdOn, suffix)}
                    {suffix === RECORD_DAY_SUFFIX && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Record Day
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Weight (lbs)</TableCell>
                {DISPLAY_SUFFIXES.map((suffix) => (
                  <TableCell key={suffix} align="center" sx={recordDayCellSx(suffix)}>
                    {formatValue(record[`weight${suffix}`])}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Weight EMA8</TableCell>
                {DISPLAY_SUFFIXES.map((suffix) => (
                  <TableCell key={suffix} align="center" sx={recordDayCellSx(suffix)}>
                    {formatValue(record[`weightEma${suffix}`])}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>Calorie Intake</TableCell>
                {DISPLAY_SUFFIXES.map((suffix) => (
                  <TableCell key={suffix} align="center" sx={recordDayCellSx(suffix)}>
                    {formatValue(record[`calorieIntake${suffix}`])}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>TDEE (smoothed)</TableCell>
                {DISPLAY_SUFFIXES.map((suffix) => (
                  <TableCell key={suffix} align="center" sx={recordDayCellSx(suffix)}>
                    {formatTdee(record[`tdeeEstimateSmoothed${suffix}`])}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell>TDEE Confidence</TableCell>
                {DISPLAY_SUFFIXES.map((suffix) => (
                  <TableCell
                    key={suffix}
                    align="center"
                    sx={{ whiteSpace: "nowrap", ...recordDayCellSx(suffix) }}
                  >
                    {formatConfidence(record[`tdeeConfidence${suffix}`])}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      </Collapse>
    </Card>
  );
}

export default function CheckIn() {
  const { themeStretch } = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const [userId, setUserId] = useState("");
  const [userInfo, setUserInfo] = useState<CheckInUserInfo | null>(null);
  const [records, setRecords] = useState<CheckInRecord[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const recentCutoff = moment().subtract(RECENT_MONTHS, "months").startOf("day");
  const isRecent = (record: CheckInRecord) =>
    moment(record.createdOn, "YYYY-MM-DD").isSameOrAfter(recentCutoff);

  const recentRecords = records.filter(isRecent);
  const olderRecords = records.filter((record) => !isRecent(record));

  const handleUserIdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId.trim()) {
      enqueueSnackbar("Please enter a user ID", { variant: "error" });
      return;
    }

    setSearchLoading(true);
    try {
      const [response, ema8DataResponse] = await Promise.all([
        getUserCheckInDetails(userId),
        getUserWeightEma8Details(userId),
      ]);
      const ema8Data = ema8DataResponse.data || {};

      if (!response.data || response.data.nutritionActionResults.length === 0) {
        setUserInfo(null);
        setRecords([]);
        enqueueSnackbar("No check-in records found for this user", { variant: "info" });
        return;
      }

      const results = { ...response.data };
      const { gender, level, heightCm, bodyfat } = results.user;
      const { mealLogData, weightLogData, tdeeData = {} } = results;
      const precisionTdee: PrecisionTdeeEntry[] = Array.isArray(results.precisionTdee)
        ? results.precisionTdee
        : [];

      const transformedRecords: CheckInRecord[] = results.nutritionActionResults.map((record: any) => {
        const date = record.createdOn;
        const daily: Record<string, number | undefined> = {};

        DISPLAY_SUFFIXES.forEach((suffix) => {
          const dateKey = dateForSuffix(date, suffix);
          const tdee: Partial<TdeeEntry> = tdeeData[dateKey] || {};
          daily[`weight${suffix}`] = weightLogData[dateKey] || 0;
          daily[`calorieIntake${suffix}`] = mealLogData[dateKey] || 0;
          daily[`weightEma${suffix}`] = ema8Data[dateKey] || 0;
          daily[`tdeeEstimateSmoothed${suffix}`] = tdee.estimateSmoothed || 0;
          daily[`tdeeConfidence${suffix}`] = tdee.confidence;
        });

        return {
          ...record,
          ...daily,
          precisionTdee: resolvePrecisionTdee(precisionTdee, date),
          gender,
          level,
          goal: record.goal,
          bodyFat: bodyfat,
          heightCm,
        };
      });

      // Sorted oldest first so each record can be compared against the target that
      // preceded it, then flipped to newest first for display.
      const chronologicalRecords = [...transformedRecords].sort((a, b) =>
        moment(a.createdOn, "YYYY-MM-DD").valueOf() - moment(b.createdOn, "YYYY-MM-DD").valueOf()
      );
      const orderedRecords = chronologicalRecords
        .map((record, index) => ({
          ...record,
          previousCalorieTarget: index > 0 ? chronologicalRecords[index - 1].calorieTarget : undefined,
        }))
        .reverse();

      // Most recent record carries the user's current target and goal.
      const latestRecord = orderedRecords[0];

      // Latest TDEE reading overall, which may be newer than the latest check-in record.
      const latestTdeeDate = Object.keys(tdeeData).sort().pop();

      setUserInfo({
        gender,
        level,
        goal: latestRecord.goal,
        bodyFat: bodyfat,
        heightCm,
        currentCalorieTarget: latestRecord.calorieTarget,
        tdee: latestTdeeDate ? tdeeData[latestTdeeDate] : undefined,
      });
      setRecords(orderedRecords);
      enqueueSnackbar(`Found ${orderedRecords.length} record(s)`, { variant: "success" });
    } catch (error) {
      console.error("Error fetching user check-in details:", error);
      enqueueSnackbar("Failed to fetch user check-in details", { variant: "error" });
      setUserInfo(null);
      setRecords([]);
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Check In | Admin Portal</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <Typography variant="h3" component="h1" paragraph>
          CheckIn
        </Typography>

        {/* User ID Search Form */}
        <form onSubmit={handleUserIdSubmit}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", maxWidth: 800 }}>
            <TextField
              label="User ID"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="Enter user ID"
              sx={{ flex: 1 }}
            />
            <LoadingButton type="submit" variant="contained" size="large" loading={searchLoading}>
              Search
            </LoadingButton>
          </Box>
        </form>

        {/* Current Information */}
        {userInfo && (
          <Card sx={{ p: 3, mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Current Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              {[
                { label: "Current Calorie Target", value: userInfo.currentCalorieTarget },
                { label: "Goal", value: userInfo.goal },
                { label: "Gender", value: userInfo.gender },
                { label: "Level", value: userInfo.level },
                { label: "Body Fat %", value: userInfo.bodyFat },
                { label: "Height (cm)", value: userInfo.heightCm },
                {
                  label: "TDEE (smoothed)",
                  value: userInfo.tdee ? formatTdee(userInfo.tdee.estimateSmoothed) : "-",
                },
                { label: "TDEE Confidence", value: formatConfidence(userInfo.tdee?.confidence) },
              ].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.label}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography variant="h6">
                      {item.value === undefined || item.value === null ? "-" : item.value}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Card>
        )}

        {/* Recent records, with the full daily breakdown */}
        {recentRecords.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Check-In Records
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
              Precision TDEE is a shadow estimator; users never saw these values.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Note:</strong> Daily data is calculated based on the user's timezone. Due to timezone
              differences, the date-specific records (weight, calories, EMA values) may vary by up to one day from the
              displayed dates.
            </Alert>
            <Stack spacing={2}>
              {recentRecords.map((record, index) => (
                <RecordCard key={`${record.createdOn} + ${record.calorieTarget} + ${index}`} record={record} />
              ))}
            </Stack>
          </Box>
        )}

        {/* Anything older than the daily-data window, summarised only */}
        {olderRecords.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Older Records
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Check-ins older than {RECENT_MONTHS} months. Daily and TDEE data is not shown for these.
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
              Precision TDEE is a shadow estimator; users never saw these values.
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Calorie Target</TableCell>
                    <TableCell>Goal</TableCell>
                    <TableCell>Pace</TableCell>
                    <TableCell>Precision TDEE</TableCell>
                    <TableCell>Weight Change</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {olderRecords.map((record, index) => (
                    <TableRow key={`${record.createdOn} + ${record.calorieTarget} + ${index}`} hover>
                      <TableCell>{record.createdOn}</TableCell>
                      <TableCell>{formatCalorieTarget(record)}</TableCell>
                      <TableCell>{record.goal}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{formatPace(record.lbsChangePerWeek)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <PrecisionTdeeValue entry={record.precisionTdee} />
                      </TableCell>
                      <TableCell>{formatWeightChange(record)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Container>
    </>
  );
}
